import { cn, formatDateTime, formatRelative, useNow } from '@/shared/lib'

type TimeAgoProps = {
  /** RFC3339 from the backend, or a Date. */
  value: string | Date
  className?: string
}

/** Past this a relative reading stops helping: «3 месяца назад» is not a date. */
const RELATIVE_WINDOW_MS = 24 * 60 * 60 * 1000

/**
 * A timestamp that ages while you look at it.
 *
 * The panel reports a system that is running right now, and every date in it
 * was frozen at the moment the page loaded — a row that said «2 минуты назад»
 * went on saying it for an hour. This re-reads on the shared clock, so the
 * screen keeps up without a refresh, which is most of what "live" means before
 * anything is actually pushed.
 *
 * Only the last day is relative. Beyond that «3 месяца назад» is vaguer than
 * the date it replaced, so the absolute form comes back. The full timestamp is
 * always in `title` either way: relative time is easier to feel and harder to
 * compare, and someone reading a log needs both.
 */
export function TimeAgo({ value, className }: TimeAgoProps) {
  const now = useNow()
  const date = value instanceof Date ? value : new Date(value)
  const absolute = formatDateTime(date)
  const isRecent = now - date.getTime() < RELATIVE_WINDOW_MS

  return (
    <time
      dateTime={date.toISOString()}
      title={absolute}
      className={cn('whitespace-nowrap', className)}
    >
      {isRecent ? formatRelative(date, new Date(now)) : absolute}
    </time>
  )
}
